import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";

import type { SoundEffectExploreItem } from "@ui/components/sound-effects/SoundEffectsExploreList";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { isXiApiUrl } from "@/app/_api/elevenlabs/fetch";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { triggerBlobDownload } from "@/app/_utilities/audioDownload";
import { logger } from "@utilities/logger";

const DOWNLOAD_COUNT_KEY = "xi:marketing-sfx-download-count";

/** Minimal shape required to initiate a download. Compatible with both SoundEffectExploreItem and GeneratedSoundItem. */
export type DownloadableSoundItem = { id: string; title: string; audioUrl?: string };

interface UseSfxDownloadParams {
  soundItems: SoundEffectExploreItem[];
  showSignUpPopover: (trigger: string) => void;
}

export function useSfxDownload({ soundItems, showSignUpPopover }: UseSfxDownloadParams) {
  const [downloadCounter, setDownloadCounter] = useLocalStorage<number>(DOWNLOAD_COUNT_KEY, 0);

  const handleDownload = useCallback(
    async (sound: DownloadableSoundItem) => {
      const position = soundItems.findIndex(s => s.id === sound.id);
      // position is -1 for generated items (not in the catalog list)
      captureEvent(EventName.mw_sfx_samples, {
        audio_list_action: "download",
        audio_list_element_id: sound.id,
        audio_list_element_position: String(position + 1),
      });

      const counter = downloadCounter ?? 0;
      if (counter >= 3) {
        showSignUpPopover("download_limit");
        return;
      }

      const rawUrl = sound.audioUrl;
      if (!rawUrl) return;

      try {
        let downloadUrl = rawUrl;
        if (isXiApiUrl(rawUrl)) {
          const token = await getCaptchaToken({ behaviourType: "sound_effects" });
          try {
            const parsedUrl = new URL(rawUrl);
            parsedUrl.searchParams.set("hcaptcha_token", token);
            if (!parsedUrl.searchParams.has("output_format")) {
              parsedUrl.searchParams.set("output_format", "mp3_44100_128");
            }
            downloadUrl = parsedUrl.toString();
          } catch {
            // Keep the original URL if URL parsing fails.
          }
        }

        const res = await fetch(downloadUrl);
        if (res.status === 429) {
          showSignUpPopover("rate_limited");
          return;
        }
        if (!res.ok) {
          logger.error(null, `Download failed with status ${res.status}`);
          return;
        }

        triggerBlobDownload(await res.blob(), sound.title);

        setDownloadCounter(counter + 1);
      } catch (err) {
        logger.error(err instanceof Error ? err : null, "Download error");
      }
    },
    [downloadCounter, setDownloadCounter, soundItems, showSignUpPopover],
  );

  return { handleDownload };
}
